/** Choosing a clinic by name, for moving a patient link, instead of pasting its id. */
import * as stylex from "@stylexjs/stylex";
import type { AdminOrganizationRow, AdminSearchResult } from "@lattice/core";
import { ROUTES } from "@lattice/core";
import { useState } from "react";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/motion.stylex";
import { motion } from "@lattice/tokens/motion.stylex";
import "@lattice/tokens/space.stylex";
import { radius, space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import { api } from "./api.ts";
import { useLoad } from "./load.ts";
import { Button, Field } from "./ui.tsx";
function Matches({
  query,
  exclude,
  chosen,
  onChoose
}: {
  readonly query: string;
  readonly exclude: string;
  readonly chosen: string | null;
  readonly onChoose: (clinic: AdminOrganizationRow) => void;
}) {
  const [found] = useLoad(`clinics-${query}`, () => api<AdminSearchResult>(ROUTES.adminSearch, {
    query: {
      q: query
    }
  }));
  if (found.state === "loading") return <p className="x1ghz6dp xl4dl4e x1jty25b">Searching</p>;
  if (found.state === "failed") return <p className="x1ghz6dp xl4dl4e x1jty25b">Clinics could not be searched. Try again.</p>;
  const clinics = found.data.organizations.filter(o => o.id !== exclude).slice(0, 5);
  if (clinics.length === 0) return <p className="x1ghz6dp xl4dl4e x1jty25b">No other clinic matches.</p>;
  return <ul className="xe8uvvx x1ghz6dp x1717udv x78zum5 xdt5ytf" aria-label="Matching clinics">
      {clinics.map(clinic => <li key={clinic.id}>
          <button type="button" aria-pressed={clinic.id === chosen} onClick={() => onChoose(clinic)} {...{
        0: {
          className: "x78zum5 xh8yej3 x1qughib x1pha0wt x626il7 x1gg8mnh xerji9v x1jyo4x1 x9f619 x1cikjda xl4dl4e x1yc453h xzb3lse xjbqb8w x171263r xiv23ze xc342km x1fbwzx x1ypdohk x15406qy x149xavy x784prv x9v5kkp x1jubnyg x1bqaal"
        },
        1: {
          className: "x78zum5 xh8yej3 x1qughib x1pha0wt x626il7 x1gg8mnh xerji9v x1jyo4x1 x9f619 x1cikjda xl4dl4e x1yc453h xzb3lse xc342km x1fbwzx x1ypdohk x15406qy x149xavy x784prv x9v5kkp x1jubnyg x1bqaal x1890um5 xqu410z"
        }
      }[!!(clinic.id === chosen) << 0]}>
            <span>{clinic.name}</span>
            <span className="x1jty25b xox936i">{clinic.slug}</span>
          </button>
        </li>)}
    </ul>;
}
export function ClinicPicker({
  exclude,
  chosen,
  onChoose
}: {
  /** The clinic the link is leaving, which is never a destination. */
  readonly exclude: string;
  readonly chosen: AdminOrganizationRow | null;
  readonly onChoose: (clinic: AdminOrganizationRow) => void;
}) {
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(chosen === null);
  const trimmed = query.trim();
  if (chosen !== null && !searching) return <div className="x78zum5 xdt5ytf x1173whf">
        <span className="xl4dl4e xqu410z">Move to clinic</span>
        <div className="x78zum5 x6s0dn4 x1qughib x626il7 x8hz100 xuih12o xqe3ids x1890um5 x1fbwzx xqu410z">
          <span>
            {chosen.name} <span className="x1jty25b xox936i">{chosen.slug}</span>
          </span>
          <Button tone="quiet" onClick={() => setSearching(true)}>
            Change
          </Button>
        </div>
      </div>;
  return <div className="x78zum5 xdt5ytf x1173whf">
      <Field id="destination" label="Move to clinic" hint="Type part of the clinic's name, then pick it." value={query} onChange={setQuery} type="search" />
      {trimmed.length < 2 ? null : <Matches query={trimmed} exclude={exclude} chosen={chosen?.id ?? null} onChoose={clinic => {
      onChoose(clinic);
      setSearching(false);
    }} />}
    </div>;
}